<script lang="ts">
    import Icon from '@iconify/svelte'
    import { cloneDeep, isEqual, sum } from 'lodash-es'

    import { gettext as _, ngettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { session, simpleFetch } from 'hydria/services/session'
    import { sanchor, spath } from 'hydria/svelte_helpers'

    import type { TldConfig } from './defs'

    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'
    import IntCurrency from 'wdom/components/IntCurrency.svelte'
    import TokenAnchorProgress from '../Users/User/TokenAnchorProgress.svelte'
    import TldConfigEditor from './TldConfigEditor.svelte'

    interface Props {
        userId?: string | null
    }

    let { userId = null }: Props = $props()

    let loading = $state(true)
    let saving = $state(false)
    let loadedUserId: string | undefined | null = $state()

    let tlds: TldConfig[] | undefined = $state()
    let savedTlds: TldConfig[] | undefined = $state()
    let configuredTlds: Set<string> = $derived(new Set(tlds ? tlds.map((tld) => tld.tld) : []))

    let changed = $derived(tlds != null && savedTlds != null && !isEqual(tlds, savedTlds))

    let domTlds: { [key: string]: string } = $state({})
    let totalDomains: number = $state(0)
    // let allTlds: Set<string> = $state(new Set())
    let allTlds: Set<string> = $derived.by(() => {
        let loc: Set<string> = new Set()
        if (savedTlds == null) return loc

        savedTlds.map((tld) => loc.add(tld.tld))
        Object.keys(domTlds).map((tld) => loc.add(tld))
        return loc
    })
    // let tldPrices: { [key: string]: number } = $state({})
    let tldPrices: { [key: string]: number } = $derived.by(() => {
        if (tlds == null) return {}
        let loc = {}
        for (const tld of tlds) {
            for (const price of tld.prices) {
                loc[`${tld.tld}:${price.n_years}`] = price.price
            }
        }
        return loc
    })
    // Indexed TLDs
    let iTlds = $derived.by(() => {
        if (tlds == null) return {}
        let loc = {}
        for (const tld of tlds) {
            loc[tld.tld] = tld
        }
        return loc
    })
    let isTlds = $derived.by(() => {
        if (savedTlds == null) return {}
        let loc = {}
        for (const tld of savedTlds) {
            loc[tld.tld] = tld
        }
        return loc
    })

    let tldStatus = $derived.by(() => {
        let loc = {}
        for (const tld of allTlds) {
            if (!isTlds[tld] && iTlds[tld]) loc[tld] = 'text-bg-success'
            else if (!iTlds[tld] && isTlds[tld]) loc[tld] = 'text-bg-danger'
            else if (!isEqual(isTlds[tld], iTlds[tld])) loc[tld] = 'text-bg-warning'
            else loc[tld] = null
        }
        return loc
    })

    async function load() {
        const userIdToLoad = userId ? userId : ''
        if (loadedUserId == userIdToLoad) return
        loadedUserId = userIdToLoad
        loading = true

        try {
            const r = await simpleFetch('GET', `/json/domain/1/tld-config/${userIdToLoad}`)
            r.data.tlds.sort((a: TldConfig, b: TldConfig) => a.tld.localeCompare(b.tld))
            savedTlds = r.data.tlds
            tlds = cloneDeep(savedTlds) as TldConfig[]
            domTlds = r.data.tld_counts
            totalDomains = sum(Object.values(domTlds))
            // let locTlds = new Set(Object.keys(r.data.tld_counts))
            // for (const tld of tlds) {
            //     locTlds.add(tld.tld)
            // }

            // allTlds = locTlds
        } catch (e: any) {
            console.error('Failed to load TLDs:', e)
            if (e && e.status == 404) {
                // This is OK, the user never had any custom TLD yet.
                savedTlds = []
                tlds = cloneDeep(savedTlds)
            } else {
                notify({
                    type: 'danger',
                    title: 'TLDs',
                    text: 'La configuration des TLDs n’a pas pu être chargée.',
                })
            }
        }

        loading = false
    }

    $effect(() => {
        load()
    })
    // onMount(() => {
    //     load()
    // })

    let limitedHeight = $state(true)
    let tldToAdd: string = $state('')
    let tldToAddOk = $derived(tldToAdd.length)

    let tldInEdition: TldConfig | null = $state(null)

    function addTld() {
        tldInEdition = {
            tld: tldToAdd.startsWith('.') ? tldToAdd.slice(1) : tldToAdd,
            in_lookup: false,
            whois_allowed: false,
            whois_price: 0,
            idna_languages: [],
            prices: [],
        }
    }

    function editTld(tld: TldConfig) {
        tldInEdition = { ...tld }
    }
    function removeTld(tld: TldConfig) {
        const i = tlds.indexOf(tld)
        if (i >= 0) {
            tlds.splice(i, 1)
            tlds = tlds
        }
    }
    function keepTldEdition() {
        if (!tldInEdition) return
        let found = false
        for (const tld of tlds) {
            if (tld.tld == tldInEdition.tld) {
                Object.assign(tld, tldInEdition)
                found = true
                break
            }
        }
        if (!found) {
            ;(tlds as TldConfig[]).push(tldInEdition)
            // tlds.sort((a: TldConfig, b: TldConfig) => a.tld.localeCompare(b.tld))
        }

        tldInEdition = null
    }

    async function save() {
        saving = true

        try {
            const r = await simpleFetch('PUT', `/json/domain/1/tld-config/${userId ? userId : ''}`, { tlds: cloneDeep(tlds) })
            r.data.tlds.sort((a: TldConfig, b: TldConfig) => a.tld.localeCompare(b.tld))
            savedTlds = r.data.tlds
            tlds = cloneDeep(savedTlds)

            notify({
                type: 'success',
                title: 'TLDs',
                text: 'Sauvegarde effectuée.',
            })
        } catch (e) {
            console.error('Failed to save TLDs:', e)
            notify({
                type: 'danger',
                title: 'TLDs',
                text: 'La sauvegarde n’a pas pu être effectuée.',
            })
        }

        saving = false
    }

    $inspect('tldPrices..', tldPrices)
</script>

<style>
    .price-list:not(:first-child):before {
        content: ', ';
        color: #999;
    }
    .idna {
        font-variant: small-caps;
    }

    td.tld {
        width: 125px;
    }
    .ndoms {
        width: 6em;
        text-align: right;
    }
    .rech {
        width: 7em;
        text-align: center;
    }
    .priv {
        width: 5em;
        text-align: right;
    }
    .idna {
        width: 4em;
        text-align: center;
    }
    .y {
        width: 5em;
        text-align: right;
    }

    .text-very-muted {
        color: #ccc;
    }
</style>

{#if tldInEdition}
    <TldConfigEditor {userId} tldConfig={tldInEdition} close={() => (tldInEdition = null)} save={(e) => keepTldEdition()} />
{/if}

<div style="display: flex; gap: 30px;">
    <div class="card" style="flex: 0 0 12em; order: 1; align-self: flex-start;">
        <div class="card-title text-primary">Actions</div>
        <div class="card-body" style="display: flex; flex-direction: column; gap: 2ex;">
            <form
                class="form"
                onsubmit={(e) => {
                    e.preventDefault()
                    addTld()
                }}
                novalidate
            >
                <label for="add-tld">Ajouter un TLD</label>
                <div class="input-group">
                    <input type="text" class="form-control" bind:value={tldToAdd} />
                    <button type="submit" class="btn btn-outline-secondary" disabled={!tldToAddOk || configuredTlds.has(tldToAdd)}><Icon inline icon="fa:plus" /></button>
                </div>
            </form>

            <div class="d-grid gap-2">
                <button
                    type="button"
                    class="btn btn-outline-secondary"
                    disabled={!changed || saving}
                    onclick={() => {
                        tlds = cloneDeep(savedTlds)
                    }}>{$_('Reset')}</button
                >
                {#if loading}
                    <div class="progress" style="margin-top: 5px">
                        <div class="progress-bar progress-bar-info progress-bar-striped active" style="width: 100%;"></div>
                    </div>
                {:else if saving}
                    <div class="progress" style="margin-top: 5px">
                        <div class="progress-bar progress-bar-warning progress-bar-striped active" style="width: 100%;"></div>
                    </div>
                {:else}
                    <button type="button" class="btn" class:btn-outline-secondary={!changed} class:btn-primary={changed} onclick={() => save()} disabled={!changed}>{$_('Sauvegarder')}</button>
                {/if}
            </div>
        </div>
    </div>

    <div class="card" style="flex: 1 0  ; order: 0;">
        <div class="card-title text-primary" style="display: flex ; align-items: center;">
            <span style="flex: 1 0 0;">{$_('Liste des TLDs')}</span>
            <span>
                <TokenAnchorProgress class="btn btn-sm btn-outline-secondary" href="/json/ecom/1/price-list/tsv">Liste en format TSV</TokenAnchorProgress>
                <button type="button" class="btn btn-sm btn-outline-secondary tt-left" data-tooltip="Limiter la hauteur de la liste" class:active={limitedHeight} onclick={() => (limitedHeight = !limitedHeight)}><Icon inline icon="fa:arrows-v" /></button>
            </span>
        </div>
        <div class="card-body" class:is-user={!!userId} style="{limitedHeight ? 'max-height: 80vh;' : ''} overflow: auto">
            <div class="table-responsive">
                <table class="table table-striped">
                    <thead>
                        <tr>
                            <th class="tld">TLD</th>
                            <th class="ndoms">N. Doms</th>
                            {#if !userId}
                                <th class="rech">Recherche</th>
                                <th class="priv">Privacy</th>
                                <th class="idna">FRA</th>
                            {/if}
                            <th class="y">1 an</th>
                            <th class="y">2 ans</th>
                            <th class="y">3 ans</th>
                            <th class="y">4 ans</th>
                            <th class="y">5 ans</th>
                            <th class="y">6 ans</th>
                            <th class="y">7 ans</th>
                            <th class="y">8 ans</th>
                            <th class="y">9 ans</th>
                            <th class="y">10 ans</th>
                            <th class="acts">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#if loading}
                            <tr>
                                <td colspan={userId ? 12 : 16}>
                                    <LoadingPattern pattern={['x', 'x', 'x']} />
                                </td>
                            </tr>
                        {:else if !tlds || !tlds.length}
                            <tr>
                                <td colspan={userId ? 12 : 16} style="text-align: center; patting: 2ex 1em">Aucune entrée</td>
                            </tr>
                        {:else}
                            <!-- {#each tlds as entry} -->
                            {#each allTlds as tld}
                                <tr>
                                    <td class="tld {tldStatus[tld]}"><span class="text-muted">.</span>{tld}</td>
                                    <td class="ndoms {tldStatus[tld]}">
                                        {#if domTlds[tld]}
                                            <a use:sanchor href={spath(`/domains/?q=.${tld}` + (userId ? ` ${userId}` : ''))}>{domTlds[tld]}</a>
                                        {:else}
                                            0
                                        {/if}
                                    </td>
                                    {#if !userId}
                                        <td class="rech {tldStatus[tld]}">
                                            {#if iTlds[tld]}
                                                <Icon icon={iTlds[tld].in_lookup ? 'fa:dot-circle-o' : 'fa:circle-o'} />
                                            {/if}
                                        </td>
                                        <td class="priv {tldStatus[tld]}">
                                            {#if iTlds[tld]}
                                                {#if iTlds[tld].whois_allowed}
                                                    <IntCurrency value={iTlds[tld].whois_price} />
                                                {:else}
                                                    <span class="text-very-muted">Non</span>
                                                {/if}
                                            {/if}
                                        </td>
                                        <td class="idna {tldStatus[tld]}">
                                            {#if iTlds[tld]}
                                                {#if iTlds[tld].idna_languages.includes('fra')}
                                                    <Icon inline icon="mdi:check" />
                                                {:else}
                                                    <span class="text-very-muted">–</span>
                                                {/if}
                                            {/if}
                                        </td>
                                    {/if}
                                    {#each [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as ny}
                                        <td class="y {tldStatus[tld]}">
                                            {#if tldPrices[`${tld}:${ny}`] != null}
                                                <IntCurrency value={tldPrices[`${tld}:${ny}`]}></IntCurrency>
                                            {:else}
                                                <span class="text-very-muted">
                                                    <IntCurrency value={0} />
                                                </span>
                                            {/if}
                                        </td>
                                    {/each}
                                    <td class="text-end {tldStatus[tld]}">
                                        <div class="btn-group btn-group-sm">
                                            {#if iTlds[tld]}
                                                <button type="button" class="btn btn-outline-secondary" onclick={() => editTld(iTlds[tld])}><Icon inline icon="fa:pencil" /></button>
                                                <button type="button" class="btn btn-outline-secondary" disabled={!iTlds[tld]} onclick={() => removeTld(iTlds[tld])}><Icon inline icon="fa:trash" /></button>
                                            {/if}
                                        </div>
                                    </td>
                                </tr>
                            {/each}
                        {/if}
                    </tbody>
                    {#if !loading}
                        <tfoot>
                            <tr>
                                <th class="tld">Total</th>
                                <th class="ndoms">{totalDomains}</th>
                                {#if !userId}
                                    <th colspan="3"></th>
                                {/if}
                                <th colspan="11"></th>
                            </tr>
                        </tfoot>
                    {/if}
                </table>
            </div>
        </div>
    </div>
</div>
